import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom'; // Import useNavigate for programmatic navigation
import { useScreenLogging } from '../../hooks/useScreenLogging';
import userService from '../../services/userService';
import './Profile.css'; // Import the CSS for the Profile component

const Profile = () => {
  // Log screen view
  useScreenLogging('Profile', {
    component: 'Profile',
  });
  const navigate = useNavigate(); // Hook for navigation
  const [userInfo, setUserInfo] = useState(null);
  const [profileImageUrl, setProfileImageUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingImage, setIsLoadingImage] = useState(false);

  // Fetch user information on component mount
  useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        setIsLoading(true);
        const user = await userService.getCurrentUser();
        setUserInfo(user);

        // Fetch profile image if user is available
        if (user && (user.userId || user.user_id)) {
          setIsLoadingImage(true);
          try {
            // First try to use the presigned URL if available
            if (user.image_presigned_url) {
              setProfileImageUrl(user.image_presigned_url);
            } else {
              // Fallback to API call
              const userId = user.userId || user.user_id;
              const imageUrl = await userService.getProfileImageUrl(userId);
              setProfileImageUrl(imageUrl);
            }
          } catch (error) {
            console.error('Error fetching profile image:', error);
            setProfileImageUrl(null);
          } finally {
            setIsLoadingImage(false);
          }
        }
      } catch (error) {
        console.error('Error fetching user information:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserInfo();
  }, []);

  // Demo user data for fallback (will be replaced with real data)
  const fallbackUserData = {
    name: 'User 1 (Chaitanya)',
    gender: 'Male',
    designation: 'Admin',
    id: 'EMP7092',
    dateOfBirth: '1988-05-20',
    mobile: '+919492207791',
    address: '654 Spruce St, City',
    bloodGroup: 'O-',
  };

  // Helper function to format date from YYYY-MM-DD to DD/MM/YYYY
  const formatDate = dateString => {
    if (!dateString || dateString === 'Not specified') {
      return 'Not specified';
    }

    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return dateString; // Return original if not a valid date
      }

      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear();

      return `${day}/${month}/${year}`;
    } catch (error) {
      console.error('Error formatting date:', error);
      return dateString; // Return original if formatting fails
    }
  };

  // Use real user data if available, otherwise fallback
  const userData = userInfo
    ? {
        name:
          userInfo.user_name ||
          userInfo.userName ||
          userInfo.displayName ||
          'User',
        gender: userInfo.gender || 'Not specified',
        designation:
          userInfo.role_name || userInfo.displayName || userInfo.role || 'User',
        id: userInfo.employee_id || userInfo.userId || 'N/A',
        dateOfBirth: formatDate(userInfo.date_of_birth),
        mobile: userInfo.phone_number || userInfo.email || 'Not specified',
        address: userInfo.address || 'Not specified',
        bloodGroup: userInfo.blood_group || 'Not specified',
      }
    : fallbackUserData;

  // Function to handle back navigation to HomePage
  const handleBackClick = () => {
    navigate('/home'); // Navigate to the HomePage path
  };

  // Function to handle settings navigation
  const handleSettingsClick = () => {
    navigate('/settings'); // Navigate to the new SettingsPage path
  };

  if (isLoading) {
    return (
      <div className="profile-page-container">
        <header className="profile-header">
          <button
            onClick={handleBackClick}
            className="back-arrow"
            aria-label="Go back to Home"
          >
            <i className="fas fa-arrow-left"></i>
          </button>
          <h1>Profile</h1>
          <button
            onClick={handleSettingsClick}
            className="settings-icon-container"
            aria-label="Go to Settings"
          >
            <i className="fas fa-cog"></i>
          </button>
        </header>
        <main className="profile-content">
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading profile...</p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="profile-page-container">
      <header className="profile-header">
        {/* Back arrow to navigate to HomePage */}
        <button
          onClick={handleBackClick}
          className="back-arrow"
          aria-label="Go back to Home"
        >
          <i className="fas fa-arrow-left"></i>
        </button>
        <h1>Profile</h1>
        {/* Settings icon to navigate to SettingsPage */}
        <button
          onClick={handleSettingsClick}
          className="settings-icon-container"
          aria-label="Go to Settings"
        >
          <i className="fas fa-cog"></i>
        </button>
      </header>

      <main className="profile-content">
        <div className="profile-avatar-container">
          {isLoadingImage ? (
            <div className="profile-avatar-loading">
              <div className="loading-spinner-small"></div>
            </div>
          ) : profileImageUrl ? (
            <img
              src={profileImageUrl}
              alt="User Profile"
              className="profile-avatar"
              onError={() => setProfileImageUrl(null)}
            />
          ) : userInfo?.userName || userInfo?.user_name ? (
            <div className="profile-avatar-initials">
              {(userInfo.userName || userInfo.user_name)
                .charAt(0)
                .toUpperCase()}
            </div>
          ) : (
            <img
              src="https://placehold.co/150x150/d3d3d3/000000?text=User+Profile"
              alt="User Profile"
              className="profile-avatar"
            />
          )}
        </div>

        <div className="profile-details-card">
          {/* Iterate over user data to display details */}
          {Object.entries(userData).map(([key, value]) => (
            <div className="detail-row" key={key}>
              <span className="detail-label">
                {key.charAt(0).toUpperCase() +
                  key.slice(1).replace(/([A-Z])/g, ' $1')}
                :
              </span>
              <span className="detail-value">{value}</span>
            </div>
          ))}
        </div>
      </main>
    </div>
  );
};

export default Profile;
